import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../SafeIcon';
import supabase from '../../supabase/supabase';

const { FiX, FiUser, FiCheck, FiAlertCircle } = FiIcons;

const EditProfileModal = ({ user, profile, onClose, onSave }) => {
  const [fullName, setFullName] = useState(profile?.full_name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ full_name: fullName, updated_at: new Date().toISOString() })
        .eq('id', user.id);
      if (updateError) throw updateError;
      setSuccess(true);
      onSave({ ...profile, full_name: fullName });
      setTimeout(onClose, 1000);
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <h3 className="font-bold text-gray-800">Edit Profile</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg text-gray-400">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-2 text-red-600 text-sm">
              <SafeIcon icon={FiAlertCircle} className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Email</label>
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-500 font-medium">
              {user?.email}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Full Name</label>
            <div className="relative">
              <SafeIcon icon={FiUser} className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-black/10 focus:border-black transition-all text-sm font-medium"
                placeholder="Your full name"
              />
            </div>
          </div>
        </div>

        <div className="p-5 bg-gray-50 border-t border-gray-100 flex space-x-3">
          <button onClick={onClose} className="flex-1 py-3 font-bold text-gray-500 hover:bg-gray-200 rounded-xl transition-colors text-sm">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || success}
            className="flex-[2] py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-black transition-colors text-sm flex items-center justify-center space-x-2 disabled:opacity-70"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : success ? (
              <><SafeIcon icon={FiCheck} className="w-4 h-4" /><span>Saved!</span></>
            ) : (
              <span>Save Changes</span>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default EditProfileModal;